import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { supabase } from '../supabaseClient' // your Supabase client
import { stripePromise, STRIPE_PRICES } from '../../utils/stripe' // your Stripe helper

export default function SubscriptionManager() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [subscriptionData, setSubscriptionData] = useState(null)
  const [processingPayment, setProcessingPayment] = useState(false)

  // If a user selects a plan and we create a subscription successfully,
  // we'll store the client secret from the subscription’s invoice PaymentIntent
  const [clientSecret, setClientSecret] = useState(null)

  useEffect(() => {
    fetchSubscriptionData()
  }, [])

  // 1. Fetch the user's subscription info from Supabase
  const fetchSubscriptionData = async () => {
    try {
      setLoading(true)
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) {
        setSubscriptionData(null)
        return
      }

      // Retrieve subscription columns from your "profiles" table
      const { data, error } = await supabase
        .from('profiles')
        .select(
          'subscription_status, subscription_price_id, subscription_current_period_end, subscription_cancel_at_period_end'
        )
        .eq('id', user.id)
        .single()

      if (error) throw error

      setSubscriptionData(data)
    } catch (err) {
      console.error(err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // 2. Called when user clicks "Subscribe"
  const handleSubscribe = async (priceId) => {
    try {
      setProcessingPayment(true)
      setError(null)

      // Use the new edge function name 'create-subscription'
      const { data, error: paymentError } = await supabase.functions.invoke('create-subscription', {
        body: { priceId },
      })

      if (paymentError) throw new Error(paymentError)

      // Extract the clientSecret from the returned subscription invoice
      const clientSecret = data?.response?.clientSecret
      if (!clientSecret) {
        throw new Error('No client secret returned from subscription creation')
      }

      setClientSecret(clientSecret)
    } catch (err) {
      console.error(err)
      setError(err.message)
    } finally {
      setProcessingPayment(false)
    }
  }

  // 3. Handle unsubscribe logic (calls another edge function, e.g. "cancel-subscription")
  const handleUnsubscribe = async () => {
    try {
      setLoading(true)
      setError(null)

      // This depends on an edge function you create (not shown here),
      // where you'd do something like:
      //    1) Look up the user profile by userId
      //    2) Call stripe.subscriptions.update(subscription_id, { cancel_at_period_end: true })
      //    3) Return updated subscription data
      const { data, error } = await supabase.functions.invoke('cancel-subscription')

      if (error) throw new Error(error.message)

      setSubscriptionData((prevData) => ({
        ...prevData,
        subscription_status: 'canceled',
        subscription_cancel_at_period_end: data.cancel_at_period_end,
        subscription_current_period_end: data.current_period_end,
      }))
    } catch (err) {
      console.error(err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // ———————————————————————————————————————————————————————
  // Render logic
  // ———————————————————————————————————————————————————————
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/** If we have a clientSecret, show PaymentForm */}
      {clientSecret && (
        <PaymentForm
          clientSecret={clientSecret}
          onCancel={() => setClientSecret(null)}
          onPaymentSuccess={() => {
            setClientSecret(null)
            fetchSubscriptionData()
          }}
        />
      )}

      {!clientSecret && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('subscription.currentStatus')}
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                {t('subscription.status')}:{' '}
                <Typography
                  component="span"
                  color={
                    subscriptionData?.subscription_status === 'active'
                      ? 'success.main'
                      : 'error.main'
                  }
                >
                  {subscriptionData?.subscription_status || 'inactive'}
                </Typography>
              </Typography>
              {subscriptionData?.subscription_status === 'active' && (
                <>
                  <Typography color="text.secondary" gutterBottom>
                    {t('subscription.renewalDate')}:{' '}
                    {subscriptionData.subscription_current_period_end
                      ? new Date(
                          subscriptionData.subscription_current_period_end
                        ).toLocaleDateString()
                      : '—'}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleUnsubscribe}
                    sx={{ mt: 2 }}
                  >
                    {t('subscription.cancel')}
                  </Button>
                </>
              )}
              {subscriptionData?.subscription_status === 'canceled' && (
                <Typography color="text.secondary" gutterBottom>
                  {t('subscription.cancelAtPeriodEnd')}:{' '}
                  {subscriptionData.subscription_cancel_at_period_end ? 'Yes' : 'No'}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/** Only show subscription plans if not active */}
          {subscriptionData?.subscription_status !== 'active' && (
            <>
              <Typography variant="h6" gutterBottom>
                {t('subscription.availablePlans')}
              </Typography>
              <Grid container spacing={3}>
                {Object.entries(STRIPE_PRICES).map(([key, priceId]) => (
                  <Grid item xs={12} sm={6} md={4} key={priceId}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {t(`subscription.plans.${key}.title`)}
                        </Typography>
                        <Typography variant="h4" component="div" gutterBottom>
                          {t(`subscription.plans.${key}.price`)}
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => handleSubscribe(priceId)}
                          disabled={processingPayment}
                        >
                          {processingPayment ? (
                            <CircularProgress size={24} />
                          ) : (
                            t('subscription.subscribe')
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}
    </Box>
  )
}

// ———————————————————————————————————————————————————————
// PaymentForm component for Payment Element
// ———————————————————————————————————————————————————————
function PaymentForm({ clientSecret, onCancel, onPaymentSuccess }) {
  const [error, setError] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const [elementsInstance, setElementsInstance] = useState(null)

  useEffect(() => {
    let isMounted = true

    async function mountPaymentElement() {
      try {
        const stripe = await stripePromise
        if (!stripe) throw new Error('Stripe did not initialize properly.')

        // 1. Initialize Elements with the clientSecret
        const elements = stripe.elements({ clientSecret })
        setElementsInstance(elements)

        // 2. Mount the Payment Element
        const paymentElement = elements.create('payment')
        paymentElement.mount('#payment-element')
      } catch (err) {
        if (isMounted) setError(err.message)
      }
    }

    mountPaymentElement()

    return () => {
      isMounted = false
    }
  }, [clientSecret])

  const handleConfirmPayment = async () => {
    try {
      if (!elementsInstance) {
        setError('Elements is not ready yet.')
        return
      }
      setConfirming(true)
      setError(null)

      const stripe = await stripePromise
      if (!stripe) throw new Error('Stripe did not initialize properly.')

      // 3. Call elements.submit() to gather payment details
      const { error: submitError } = await elementsInstance.submit()
      if (submitError) {
        throw new Error(submitError.message)
      }

      // 4. Confirm the payment
      const { error: confirmError } = await stripe.confirmPayment({
        elements: elementsInstance,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin + '/settings',
        },
      })
      if (confirmError) {
        throw new Error(confirmError.message)
      }

      // 5. If NOT redirecting automatically, handle success
      onPaymentSuccess()
    } catch (err) {
      setError(err.message)
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box id="payment-element" sx={{ mb: 3 }} />
      <Box display="flex" gap={2}>
        <Button variant="outlined" onClick={onCancel} disabled={confirming}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirmPayment} disabled={confirming}>
          {confirming ? <CircularProgress size={24} /> : 'Confirm Payment'}
        </Button>
      </Box>
    </Box>
  )
}
